import { createSlice } from "@reduxjs/toolkit";
import { DEFAULT_CONFIGURATION, STEPS } from "../../../Common/store/data/commonData";
import {
  closeSnackbarImplementation,
  setCurrentStepImplementation,
  setSavedConfigurationsImplementation,
  showMinimalPopupImplementation,
  showSnackbarImplementation
} from "../../../Common/store/implementations/commonImplementation";
import t from "../../../Common/utils/translation/translator";
import {
  saveApplicationDataDependenciesImplementation,
  setApplicationSettingsChangedImplementation,
  setApplicationSettingsImplementation,
  setProductsDescriptionsImplementation,
  setSavedMotorDependenciesPayloadImplementation
} from "../implementations/retrofitImplementation";

const steps = STEPS.RETROFIT;

export const DEFAULT_SIZING_MECHANICS = {
  isMechanics: "General loading case",
  feedConstant: "",
  momentOfInertiaLoad: "",
  kineticFrictionTorque: "",
  additionalTorqueConstant: "",
  viscousFrictionTorque: "",
  feedConstantUnit: "mm.1-1",
  momentOfInertiaLoadUnit: "",
  kineticFrictionTorqueUnit: "",
  additionalTorqueConstantUnit: "",
  viscousFrictionTorqueUnit: "",
  diameterDriveRoll: "",
  massLoad: "",
  massBelt: "",
  momentOfInertiaDriveRoll: "",
  momentOfInertiaDrivenRoll: "",
  momentOfInertiaAdditionalRoll: "",
  additionalMomentOfInertia: "",
  kineticFrictionForce: "",
  materialTensionForce: "",
  additionalConstantForce: "",
  viscousFrictionforce: "",
  diameterDriveRollUnit: "mm",
  massLoadUnit: "kg",
  massBeltUnit: "kg",
  momentOfInertiaDriveRollUnit: "kg.cm²",
  momentOfInertiaDrivenRollUnit: "kg.cm²",
  momentOfInertiaAdditionalRollUnit: "kg.cm²",
  additionalMomentOfInertiaUnit: "",
  kineticFrictionForceUnit: "",
  materialTensionForceUnit: "",
  additionalConstantForceUnit: "",
  viscousFrictionforceUnit: "",
  inclinationAngle: "",
  inclinationAngleUnit: "",
  threadPitch: "",
  efficiency: "",
  threadPitchUnit: "mm",
  efficiencyUnit: "%",
  crankArmLength: "",
  pushRodLength: "",
  xStart: "",
  offsetFromSlideLevelToCrankShaft: "",
  minimalPositionOfTheSlide: "",
  maximalPositionOfTheSlide: "",
  crankArmLengthUnit: "mm",
  pushRodLengthUnit: "mm",
  xStartUnit: "mm",
  offsetFromSlideLevelToCrankShaftUnit: "mm",
  minimalPositionOfTheSlideUnit: "mm",
  maximalPositionOfTheSlideUnit: "mm",
  pinion: "Tooth",
  toothPitch: "",
  numberOfTeeth: "",
  massRack: "",
  toothPitchUnit: "mm",
  massRackUnit: "kg",
  isValid: false
};

export const DEFAULT_SIZING_DYNAMICS = {
  motionProfile: "Positioning Diagram",
  name: "",
  description: "",
  secondsCycle: "",
  cycleMinutes: "",
  xOffset: "",
  numberOfSegments: "",
  segments: [],
  isValid: false
};

export const DEFAULT_SIZING_GEARBOX_SELECTION = {
  isGearBox: false,
  gearRatio: "",
  momentOfInertia: "",
  efficiency: "",
  kineticFrictionTorque: "",
  mViscous: "",
  gearRatioUnit: "",
  momentOfInertiaUnit: "",
  efficiencyUnit: "",
  kineticFrictionTorqueUnit: "",
  mViscousUnit: "",
  isValid: false
};

export const DEFAULT_SIZING = {
  mechanics: DEFAULT_SIZING_MECHANICS,
  dynamics: DEFAULT_SIZING_DYNAMICS,
  gearBox: DEFAULT_SIZING_GEARBOX_SELECTION
};

const initialState = {
  filtersInit: null,
  steps,
  currentStep: steps.START,
  currentSizingStep: STEPS.RETROFIT_SIZING.DEFINE_MECHANICS,
  possibleFiltersValues: null,
  nextStep: steps.DEVICES,
  configuration: null,
  able: true,
  defaultConfiguration: {
    ...DEFAULT_CONFIGURATION,
    technology: "Servo Drives",
    configurationType: "RETROFIT",
    currentAxisId: 1,
    applicationSettings: null,
    axes: [],
    sizing: DEFAULT_SIZING
  },
  currentAxisId: null,
  readOnlyMode: false,
  showReadOnlyDialogPopup: false,
  showDeleteConfirmationPopup: false,
  showEmailPopup: false,
  showAxisPopup: false,
  newAxisMode: false,
  currentFiltersStepperStep: 1,
  // used to refetch configurations from different components
  reFetchConfigurations: true,
  minimalPopup: {
    show: false,
    message: ""
  },
  showContinueOnDesktopPopup: false,
  // FILTERS
  fetchedBundles: [],
  countOfBundles: 0,
  rowsToShow: 20,
  // At init -> limit = rowsToShow
  paging: { skip: 0, limit: 20 },
  // orderBy is used to sort the bundles in the table
  orderBy: {
    attributeName: "nominalTorque",
    sort: "desc"
  },
  // comparator
  showOnlyDifferentValues: false,

  // Motor, Drive or Gearbox
  filtersWrapperOpened: null,

  emailData: {
    captchaResponse: null,
    linkToTool: window.location.href,
    message: null,
    recipientEmail: null,
    recipientName: null,
    senderEmail: null,
    senderName: null,
    subject: "Bill of Material - %s - %s - Schneider Electric"
  },

  configurationIdToShare: null,

  // used to optimize the functionnality of the filters dependencies
  sychronizedFilters: {
    driveFilters: null,
    motorFilters: null,
    gearboxFilters: null,
    nominalTorqueUserInput: null,
    inertiaRatioUserInput: null
  },

  // the saved state of the filters (payload) after running the filters dependencies
  savedDependenciesPayloads: {
    // saved filters (all filters) when the drive dependency is ran
    drive: null,
    // saved filters when the motor dependency is ran
    motor: null,
    // saved filters when the gearbox dependency is ran
    gearbox: null
  },

  isSynchronizingAppDataFilters: false,
  filtersPopupOpened: false,
  isSizingSelected: false,
  // axes to display in specify/select step (all (default) / without bundle / with bundle)
  axesToDisplay: "all",
  itemToHighlight: null,
  // Snackbar
  snackbar: {
    show: false
  },

  mobileBundleInfoPopup: { show: false },

  fetchBundlesPayload: null,
  highlightApplicationSettingsErrors: false,

  // used to know if the application settings have been changed to trigger the notification
  applicationSettingsChanged: false,

  partnerPreferences: null,

  // edit axis popup
  editingAxis: false,

  updateConfiguration: false,
  launchSyncAppData: false,

  savedConfigurations: []
};

export const retrofit = createSlice({
  name: "retrofit",
  initialState,
  reducers: {
    resetRetrofit: (state) => {
      const resetState = { ...initialState };
      resetState.possibleFiltersValues = state.possibleFiltersValues;
      resetState.defaultConfiguration = state.defaultConfiguration;
      resetState.filtersInit = state.filtersInit;
      resetState.partnerPreferences = state.partnerPreferences;
      return resetState;
    },

    setCurrentStep: (state, action) => {
      setCurrentStepImplementation(state, action);
    },
    setCurrentSizingStep: (state, action) => {
      state.currentSizingStep = action.payload;

      const { readOnlyMode } = state;
      if (readOnlyMode) {
        return;
      }
      // update it in the configuration
      const { configuration } = state;
      if (configuration && configuration.configData.configStatus !== "FINISHED") {
        configuration.configData.currentSizingStep = action.payload.id;
        // update the config status
        configuration.configData.configStatus = action.payload.status;
      } else {
        console.log("status = FINISHED OR configuration is null => setCurrentStep can't be done");
      }
    },
    setSizingMechanics: (state, action) => {
      const { configuration } = state;
      const mechanics = action.payload;
      configuration.configData.sizing.mechanics = mechanics;
    },
    setSizingDynamics: (state, action) => {
      const { configuration } = state;
      const dynamics = action.payload;
      configuration.configData.sizing.dynamics = dynamics;
    },
    setSizingGearBox: (state, action) => {
      const { configuration } = state;
      const gearBox = action.payload;
      configuration.configData.sizing.gearBox = gearBox;
    },
    setCurrentAxisId: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.currentAxisId = action.payload;

      // update it in the configuration
      const { configuration } = state;
      configuration.configData.currentAxisId = action.payload;
    },
    setStartConfiguration: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.startConfiguration = action.payload;
    },
    setNextStep: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.nextStep = action.payload;
    },
    setConfiguration: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.configuration = action.payload;

      if (state.configuration) {
        // set currentAxisId and currentStepId
        // eslint-disable-next-line no-param-reassign
        state.currentAxisId = action.payload.configData.currentAxisId;

        const stepsAsArray = Object.values(state.steps);
        // eslint-disable-next-line no-param-reassign
        state.currentStep = stepsAsArray.find((step) => step.id === action.payload.configData.currentStepId);
      }
    },

    setConfigData: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;

      // eslint-disable-next-line no-param-reassign
      configuration.configData = action.payload;
    },
    addNewAxis: (state) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const newId = configuration.configData.axes.reduce((max, axis) => Math.max(max, axis.id), 0) + 1;

      const { filtersInit } = state;

      // if applicaton settings (configData.applicationSettings) are defined =>
      // set nbPhases, driveVoltages, driveInterface into driveFilters (in filtersInit)

      const { applicationSettings } = configuration.configData;

      const newAxis = {
        id: newId,
        name: `${t("msizer.ui.axis")} ${newId}`,
        bundle: {
          motor: null,
          drive: null,
          gearbox: null
        },
        configFilters: {
          ...filtersInit,
          driveFilter: {
            ...filtersInit.driveFilter,
            ...applicationSettings
          },
          motorFilter: {
            ...filtersInit.motorFilter,
            ipDegreeOfProtection: ["IP54"],
            holdingBrake: [false]
          },
          gearboxFilter: {
            ...filtersInit.gearboxFilter,
            enabled: false
          }
        },
        axisState: {
          bundlesToCompare: [],
          correctDependency: true,
          applicationDataFiltersApplied: false
        },
        collapsed: true
      };

      configuration.configData.axes.push(newAxis);
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },
    addNewAxisForCartesian: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;

      if (readOnlyMode) {
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration, filtersInit } = state;

      const selectedAxis = action.payload;
      const newId = configuration.configData.axes.reduce((max, axis) => Math.max(max, axis.id), 0) + 1;

      const getMotorFlangeSizeRange = (axisSize) => {
        switch (axisSize) {
          case 1:
            return { min: 0, max: 60 };
          case 2:
            return { min: 60, max: 80 };
          case 3:
            return { min: 80, max: 120 };
          case 4:
            return { min: 120, max: 160 };
          default:
            return { min: 0, max: 9999 };
        }
      };

      const getGearboxExternalDiameter = (axisSize) => {
        switch (axisSize) {
          case 1:
          case 2:
            return [60];
          case 3:
            return [80];
          case 4:
            return [120];
          default:
            return [];
        }
      };

      const axisType = selectedAxis?.axis.driveType;
      const axisSize = selectedAxis?.axisProfile?.axisProfileSize;

      const newConfigFilters = {
        ...filtersInit,
        driveFilter: {
          ...filtersInit.driveFilter,
          driveInterface: ["Ethernet/IP"],
          driveVoltages: ["380...480 V", "400 V", "208...480 V", "380...440 V"],
          driveVoltagesSplitted: [400],
          nbPhases: ["3 Phases"]
        },
        motorFilter: {
          ...filtersInit.motorFilter,
          shaftEnd: ["smooth_shaft"],
          ipDegreeOfProtection: ["IP54"],
          holdingBrake: [false],
          motorEncoder: ["single_turn"],
          connectorType: ["Angled"],
          flangeSize: getMotorFlangeSizeRange(axisSize)
        },
        gearboxFilter: {
          ...filtersInit.gearboxFilter,
          enabled: true,
          productOrComponentType: ["PLANETARY_GEARBOX"],
          ratio: [3, 5, 8],
          externalDiameter: getGearboxExternalDiameter(axisSize)
        }
      };

      if (axisType === "toothed belt") {
        newConfigFilters.gearboxFilter.enabled = true;
      } else if (axisType === "ballscrew") {
        newConfigFilters.gearboxFilter.enabled = false;
      } else {
        newConfigFilters.gearboxFilter.enabled = false;
      }

      // Define the new axis object
      const newAxis = {
        id: newId,
        name: `${t("msizer.ui.axis")} ${newId}`,
        bundle: {
          motor: null,
          drive: null,
          gearbox: null
        },
        configFilters: newConfigFilters,
        axisState: {
          bundlesToCompare: [],
          correctDependency: true,
          applicationDataFiltersApplied: false
        },
        collapsed: true
      };

      configuration.configData.axes.push(newAxis);
      state.configuration = configuration;
    },
    deleteAxis: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;

      if (configuration.configData.axes.length === 1) {
        return;
      }

      configuration.configData.axes = configuration.configData.axes.filter((axis) => axis.id !== action.payload);
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;

      // verify if the deleted axe was the currentAxe => assign the first axis ID as currentAxisId
      if (state.currentAxisId === action.payload) {
        // eslint-disable-next-line no-param-reassign
        state.currentAxisId = configuration.configData.axes[0]?.id;
        // update it in the configuration
        configuration.configData.currentAxisId = configuration.configData.axes[0]?.id;
      }
    },
    deleteAllAxisForCartesian: (state) => {
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      configuration.configData.axes = [];
    },
    duplicateAxis: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const newId = configuration.configData.axes.reduce((max, axis) => Math.max(max, axis.id), 0) + 1;
      const newAxis = {
        id: newId,
        name: `${t("msizer.ui.specify.name_duplication_prefix")} ${action.payload.name}`,
        bundle: action.payload.bundle,
        configFilters: action.payload.configFilters,
        axisState: action.payload.axisState
      };

      configuration.configData.axes.push(newAxis);
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setApplicationDataFilter: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      // const { applicationDataFilter } = action.payload;

      configuration.configData.axes.find(
        (axis) => axis.id === action.payload.axisId
      ).configFilters.applicationDataFilter = action.payload.applicationDataFilter;
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },
    setAxisBundleData: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const { axisId, bundleData } = action.payload;

      configuration.configData.axes.find((axis) => axis.id === axisId).bundle.bundleData = bundleData;

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setBundle: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration, currentAxisId } = state;
      const bundle = action.payload;

      const newBundle = {
        motorReference: bundle.motor.commercialReference,
        driveReference: bundle.drive.commercialReference,
        gearboxReference: bundle.gearbox ? bundle.gearbox.commercialReference : null,
        adaptationKitReference: bundle.adaptationKit ? bundle.adaptationKit.commercialReference : null,
        bundleData: bundle
      };

      configuration.configData.axes.find((axis) => axis.id === currentAxisId).bundle = newBundle;

      // collapse all axes except the current one
      configuration.configData.axes.forEach((axis) => {
        if (axis.id === currentAxisId) {
          // eslint-disable-next-line no-param-reassign
          axis.collapsed = false;
        } else {
          // eslint-disable-next-line no-param-reassign
          axis.collapsed = true;
        }
      });
      configuration.configData.axes.find((axis) => axis.id === currentAxisId).collapsed = false;

      // set itemToHighlight tp "Axis-" + currentAxisId  to highlight the updated
      // eslint-disable-next-line no-param-reassign
      state.itemToHighlight = `Axis-${currentAxisId}`;

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    unsetBundle: (state) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration, currentAxisId } = state;

      configuration.configData.axes.find((axis) => axis.id === currentAxisId).bundle = {};

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setReadOnlyMode: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.readOnlyMode = action.payload;
    },

    setShowReadOnlyDialogPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showReadOnlyDialogPopup = action.payload;
    },

    setShowDeleteConfirmationPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showDeleteConfirmationPopup = action.payload;
    },

    setConfigurationStatus: (state, action) => {
      const { configuration } = state;
      const { status } = action.payload;

      configuration.configData.configStatus = status;
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setReFetchConfigurations: (state) => {
      const { reFetchConfigurations } = state;
      // eslint-disable-next-line no-param-reassign
      state.reFetchConfigurations = !reFetchConfigurations;
    },

    setAxisFilters: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const { axisId, filters } = action.payload;

      if (!axisId) {
        return;
      }

      configuration.configData.axes.find((axis) => axis.id === axisId).configFilters = filters;
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setShowEmailPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showEmailPopup = action.payload;
    },

    setShowAxisPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showAxisPopup = action.payload;
    },

    setShowContinueOnDesktopPopup: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showContinueOnDesktopPopup = action.payload;
    },

    setCurrentFiltersStepperStep: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.currentFiltersStepperStep = action.payload;
    },

    // FILTERS
    setFetchedBundles: (state, action) => {
      const { paginatedBundles, countOfResults, fetchBundlesPayload } = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.fetchedBundles = paginatedBundles;
      // eslint-disable-next-line no-param-reassign
      state.fetchBundlesPayload = fetchBundlesPayload;
      if (countOfResults) {
        // eslint-disable-next-line no-param-reassign
        state.countOfBundles = countOfResults;
      } else {
        // eslint-disable-next-line no-param-reassign
        state.countOfBundles = 0;
      }
    },

    addBundleToCompare: (state, action) => {
      // eslint-disable-next-line max-len
      // add bundle object to the bundlesToCompare array (which is in the axisState of the currentAxis of the configuration) if it doesn't exist, otherwise remove it (maximum 3 items) (axisState can be an empty object, so for the first occurence, we have to initialize it)

      const { configuration } = state;
      const { currentAxisId } = state;
      const bundle = action.payload;

      const axis = configuration.configData.axes.find((a) => a.id === currentAxisId);

      if (!axis.axisState.bundlesToCompare) {
        axis.axisState.bundlesToCompare = [];
      }

      // verify if the bundlesToCompare contains the bundle
      const bundleToCompare = axis.axisState.bundlesToCompare.find((b) => b.id === bundle.id);

      if (bundleToCompare) {
        // remove the bundle from the array
        axis.axisState.bundlesToCompare = axis.axisState.bundlesToCompare.filter((b) => b.id !== bundle.id);
      } else if (axis.axisState.bundlesToCompare.length < 3) {
        // add the bundle to the array if it has less than 3 items
        axis.axisState.bundlesToCompare.push(bundle);
      }

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    clearBundlesToCompare: (state) => {
      const { configuration } = state;
      const { currentAxisId } = state;

      const axis = configuration?.configData?.axes?.find((a) => a.id === currentAxisId);

      if (!axis) {
        return;
      }

      axis.axisState.bundlesToCompare = [];

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    applyApplicationDataFilters: (state) => {
      const { configuration } = state;
      const { currentAxisId } = state;

      const axis = configuration.configData.axes.find((a) => a.id === currentAxisId);

      axis.axisState.applicationDataFiltersApplied = true;
      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setShowOnlyDifferentValues: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.showOnlyDifferentValues = action.payload;
    },

    showMinimalPopup: (state, action) => {
      showMinimalPopupImplementation(state, action);
    },

    closeMinimalPopup: (state) => {
      // eslint-disable-next-line no-param-reassign
      state.minimalPopup = {
        show: false,
        message: ""
      };
    },

    setPossibleFiltersValues: (state, action) => {
      const possibleFiltersValuesFromS3 = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.possibleFiltersValues = possibleFiltersValuesFromS3;

      // set filtersInit
      const filtersInitFromPossibleValues = {
        applicationDataFilter: {
          nominalTorque: "",
          nominalSpeed: "",
          loadInertia: "",
          maxLength: ""
        },
        motorFilter: {
          withGearbox: undefined,
          nominalTorque: {
            min: possibleFiltersValuesFromS3.motor.nominalTorque.min,
            max: possibleFiltersValuesFromS3.motor.nominalTorque.max
          },
          nominalSpeed: {
            min: possibleFiltersValuesFromS3.motor.nominalSpeed.min,
            max: possibleFiltersValuesFromS3.motor.nominalSpeed.max
          },
          flangeSize: {
            min: possibleFiltersValuesFromS3.motor.flangeSize.min,
            max: possibleFiltersValuesFromS3.motor.flangeSize.max
          },
          motorType: undefined,
          connectorType: [],
          motorLength: {
            min: possibleFiltersValuesFromS3.motor.motorLength.min,
            max: possibleFiltersValuesFromS3.motor.motorLength.max
          },
          shaftEnd: [],
          ipDegreeOfProtection: [],
          holdingBrake: [],
          feedbackType: [],
          motorEncoder: []
        },
        driveFilter: {
          driveType: undefined,
          driveInterface: [],
          outputCurrent: [],
          supplyVoltage: [],
          driveVoltages: [],
          nbPhases: []
        },
        gearboxFilter: {
          enabled: undefined,
          manufacturer: [],
          gearboxType: [],
          ratio: [],
          gearboxPrecision: [],
          specification: "",
          efficiency: [],
          shaftMomentOfInertia: {
            min: possibleFiltersValuesFromS3.gearbox.shaftMomentOfInertia.min,
            max: possibleFiltersValuesFromS3.gearbox.shaftMomentOfInertia.max
          },
          continuousOutputTorque: {
            min: possibleFiltersValuesFromS3.gearbox.continuousOutputTorque.min,
            max: possibleFiltersValuesFromS3.gearbox.continuousOutputTorque.max
          },
          family: [],
          // productOrComponentType: ["PLANETARY_GEARBOX"],
          productOrComponentType: [],
          inputDiameter: [],
          maximumOutputTorque: {
            min: possibleFiltersValuesFromS3.gearbox.maximumOutputTorque.min,
            max: possibleFiltersValuesFromS3.gearbox.maximumOutputTorque.max
          },
          torsionalBacklash: {
            min: 0,
            max: Math.max(...possibleFiltersValuesFromS3.gearbox.torsionalBacklash)
          }
        },
        bundlesToCompare: []
      };
      // eslint-disable-next-line no-param-reassign
      state.filtersInit = filtersInitFromPossibleValues;
    },

    setApplicationSettings: (state, action) => {
      setApplicationSettingsImplementation(state, action);
    },

    setApplicationSettingsChanged: (state, action) => {
      setApplicationSettingsChangedImplementation(state, action);
    },
    // update the axis name of the given axis id
    updateAxisName: (state, action) => {
      /* READ ONLY VERIFICATION */
      const { readOnlyMode } = state;
      if (readOnlyMode) {
        // eslint-disable-next-line no-param-reassign
        state.showReadOnlyDialogPopup = true;
        return;
      }

      const { configuration } = state;
      const { axisId, name } = action.payload;
      const axis = configuration.configData.axes.find((a) => a.id === axisId);

      axis.name = name;

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    collapseAxisDetails: (state, action) => {
      const { configuration } = state;
      const axisId = action.payload;
      const axis = configuration.configData.axes.find((a) => a.id === axisId);

      // eslint-disable-next-line no-param-reassign
      axis.collapsed = !axis.collapsed;

      // eslint-disable-next-line no-param-reassign
      state.configuration = configuration;
    },

    setFiltersWrapperOpened: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.filtersWrapperOpened = action.payload;
    },

    setRowsToShow: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.rowsToShow = action.payload;
    },

    setPaging: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.paging = action.payload;
    },

    setOrderBy: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.orderBy = action.payload;
    },

    setStoredEmailData: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.emailData = action.payload;
    },

    setProductsDescriptions: (state, action) => {
      setProductsDescriptionsImplementation(state, action);
    },

    setMotorSynchronizedFilters: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.sychronizedFilters.motorFilters = action.payload;
    },

    setDriveSynchronizedFilters: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.sychronizedFilters.driveFilters = action.payload;
    },

    setGearboxSynchronizedFilters: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.sychronizedFilters.gearboxFilters = action.payload;
    },

    setNominalTorqueUserInput: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.sychronizedFilters.nominalTorqueUserInput = action.payload;
    },

    setInertiaRatioUserInput: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.sychronizedFilters.inertiaRatioUserInput = action.payload;
    },

    setSavedDriveDependenciesPayload: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.savedDependenciesPayloads.drive = action.payload;
    },

    setSavedMotorDependenciesPayload: (state, action) => {
      setSavedMotorDependenciesPayloadImplementation(state, action);
    },

    setSavedGearboxDependenciesPayload: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.savedDependenciesPayloads.gearbox = action.payload;
    },

    setConfigurationIdToShare: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.configurationIdToShare = action.payload;
    },

    setConfigurationTypeToShare: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.configurationTypeToShare = action.payload;
    },

    saveApplicationDataDependencies: (state, action) => {
      saveApplicationDataDependenciesImplementation(state, action);
    },

    setIsSynchronizingAppDataFilters: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.isSynchronizingAppDataFilters = action.payload;
    },

    setFiltersPopupOpened: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.filtersPopupOpened = action.payload;
    },
    setIsSizingSelected: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.isSizingSelected = action.payload;
    },
    setAxesToDisplay: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.axesToDisplay = action.payload;
    },

    setItemToHighlight: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.itemToHighlight = action.payload;
    },
    showSnackbar: (state, action) => {
      showSnackbarImplementation(state, action);
    },
    closeSnackbar: (state, action) => {
      closeSnackbarImplementation(state, action);
    },

    showMobileBundleInfoPopup: (state, action) => {
      const { solution } = action.payload;
      const { detailRef } = action.payload;
      // eslint-disable-next-line no-param-reassign
      state.mobileBundleInfoPopup = {
        solution,
        detailRef,
        show: true
      };
    },

    closeMobileBundleInfoPopup: (state) => {
      // eslint-disable-next-line no-param-reassign
      state.mobileBundleInfoPopup = {
        show: false
      };
    },

    setNewAxisMode: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.newAxisMode = action.payload;
    },

    setHighlightApplicationSettingsErrors: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.highlightApplicationSettingsErrors = action.payload;
    },

    setPartnerPreferences: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.partnerPreferences = action.payload;
    },

    setEditingAxis: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.editingAxis = action.payload;
    },

    setLaunchSyncAppData: (state, action) => {
      // eslint-disable-next-line no-param-reassign
      state.launchSyncAppData = action.payload;
    },

    setRetrofitSavedConfigurations: (state, action) => {
      setSavedConfigurationsImplementation(state, action);
    }
  }
});

export const {
  setCurrentStep,
  setCurrentSizingStep,
  setSizingMechanics,
  setSizingDynamics,
  setSizingGearBox,
  setNextStep,
  setConfiguration,
  setConfigData,
  showSnackbar,
  closeSnackbar,
  showMobileBundleInfoPopup,
  closeMobileBundleInfoPopup,
  setNewAxisMode,

  // Axis actions
  addNewAxis,
  addNewAxisForCartesian,
  deleteAxis,
  deleteAllAxisForCartesian,
  duplicateAxis,
  setCurrentAxisId,

  setApplicationDataFilter,
  setBundle,
  unsetBundle,
  setAxisBundleData,

  setReadOnlyMode,
  setShowReadOnlyDialogPopup,
  setShowDeleteConfirmationPopup,
  setConfigurationStatus,
  setReFetchConfigurations,
  setAxisFilters,
  setShowEmailPopup,
  setShowAxisPopup,
  setShowContinueOnDesktopPopup,
  setCurrentFiltersStepperStep,

  // FILTERS
  setFetchedBundles,
  addBundleToCompare,
  clearBundlesToCompare,
  applyApplicationDataFilters,

  setShowOnlyDifferentValues,
  showMinimalPopup,
  closeMinimalPopup,

  // default filters from S3
  setPossibleFiltersValues,
  setApplicationSettings,
  updateAxisName,
  collapseAxisDetails,

  setFiltersWrapperOpened,
  setRowsToShow,
  setPaging,
  setOrderBy,
  setStoredEmailData,
  setProductsDescriptions,

  // filters dependencies
  setMotorSynchronizedFilters,
  setDriveSynchronizedFilters,
  setGearboxSynchronizedFilters,
  setNominalTorqueUserInput,
  setInertiaRatioUserInput,

  setSavedDriveDependenciesPayload,
  setSavedMotorDependenciesPayload,
  setSavedGearboxDependenciesPayload,
  saveApplicationDataDependencies,

  setIsSynchronizingAppDataFilters,

  setSavedFilters,

  setConfigurationIdToShare,
  setConfigurationTypeToShare,

  setFiltersPopupOpened,
  setIsSizingSelected,
  setAxesToDisplay,
  setItemToHighlight,

  setHighlightApplicationSettingsErrors,

  setApplicationSettingsChanged,

  setPartnerPreferences,

  setEditingAxis,

  setLaunchSyncAppData,
  setRetrofitSavedConfigurations,

  resetRetrofit
} = retrofit.actions;

// selectors
export const selectRetrofitSteps = (state) => state.retrofit.steps;
export const selectRetrofitCurrentStep = (state) => state.retrofit.currentStep;
export const selectRetrofitSizingCurrentStep = (state) => state.retrofit.currentSizingStep;
export const selectPossibleFiltersValues = (state) => state.retrofit.possibleFiltersValues;
export const selectNextStep = (state) => state.retrofit.nextStep;
export const selectRetrofitConfiguration = (state) => state?.retrofit?.configuration;
export const selectSnackbar = (state) => state.retrofit.snackbar;
export const selectMobileBundleInfoPopup = (state) => state.retrofit.mobileBundleInfoPopup;
export const selectNewAxisMode = (state) => state.retrofit.newAxisMode;
export const selectDefaultRetrofitConfiguration = (state) => state.retrofit.defaultConfiguration;

export const selectCurrentAxisId = (state) => state.retrofit.currentAxisId;

export const selectReadOnlyMode = (state) => state.retrofit.readOnlyMode;
export const selectShowReadOnlyDialogPopup = (state) => state.retrofit.showReadOnlyDialogPopup;
export const selectShowDeleteConfirmationPopup = (state) => state.retrofit.showDeleteConfirmationPopup;
export const selectReFetchConfigurations = (state) => state.retrofit.reFetchConfigurations;
export const selectConfigAxes = (state) => state.retrofit.configuration?.configData?.axes;
export const selectFiltersInit = (state) => state.retrofit.filtersInit;
// select filters of the current axis
export const selectCurrentAxisFilters = (state) => {
  const { configuration, currentAxisId } = state.retrofit;
  const axis = configuration?.configData?.axes?.find((a) => a.id === currentAxisId);
  return axis?.configFilters;
};
export const selectShowEmailPopup = (state) => state.retrofit.showEmailPopup;
export const selectShowAxisPopup = (state) => state.retrofit.showAxisPopup;
export const selectShowContinueOnDesktopPopup = (state) => state.retrofit.showContinueOnDesktopPopup;
export const selectCurrentFiltersStepperStep = (state) => state.retrofit.currentFiltersStepperStep;

// FILTERS
export const selectFetchedBundles = (state) => state.retrofit.fetchedBundles;
export const selectCountOfBundles = (state) => state.retrofit.countOfBundles;
export const selectBundlesToCompare = (state) => {
  const { configuration, currentAxisId } = state.retrofit;
  const axis = configuration?.configData?.axes?.find((a) => a.id === currentAxisId);
  return axis?.axisState?.bundlesToCompare;
};
export const selectSelectedBundle = (state) => {
  const { configuration, currentAxisId } = state.retrofit;
  const axis = configuration.configData.axes.find((a) => a.id === currentAxisId);
  return axis?.bundle?.bundleData;
};
export const selectShowOnlyDifferentValues = (state) => state.retrofit.showOnlyDifferentValues;
export const selectMinimalPopup = (state) => state.retrofit.minimalPopup;
export const selectFiltersWrapperOpened = (state) => state.retrofit.filtersWrapperOpened;
export const selectRowsToShow = (state) => state.retrofit.rowsToShow;
export const selectPaging = (state) => state.retrofit.paging;
export const selectOrderBy = (state) => state.retrofit.orderBy;
export const selectEmailData = (state) => state?.retrofit?.emailData;

export const selectMotorSynchronizedFilters = (state) => state.retrofit.sychronizedFilters?.motorFilters;

export const selectDriveSynchronizedFilters = (state) => state.retrofit.sychronizedFilters?.driveFilters;

export const selectGearboxSynchronizedFilters = (state) => state.retrofit.sychronizedFilters?.gearboxFilters;

export const selectNominalTorqueUserInput = (state) => state.retrofit.sychronizedFilters?.nominalTorqueUserInput;

export const selectInertiaRatioUserInput = (state) => state.retrofit.sychronizedFilters?.inertiaRatioUserInput;

export const selectSavedDriveDependenciesPayload = (state) => state.retrofit.savedDependenciesPayloads?.drive;

export const selectSavedMotorDependenciesPayload = (state) => state.retrofit.savedDependenciesPayloads?.motor;

export const selectSavedGearboxDependenciesPayload = (state) => state.retrofit.savedDependenciesPayloads?.gearbox;

export const selectIsSynchronizingAppDataFilters = (state) => state.retrofit.isSynchronizingAppDataFilters;

export const selectConfigurationIdToShare = (state) => state.retrofit.configurationIdToShare;
export const selectConfigurationTypeToShare = (state) => state.retrofit.configurationTypeToShare;
export default retrofit.reducer;

export const selectApplicationSettings = (state) => state.retrofit.configuration?.configData?.applicationSettings;

export const selectFiltersPopupOpened = (state) => state.retrofit.filtersPopupOpened;
export const selectSizingSelected = (state) => state.retrofit.isSizingSelected;
export const selectFetchBundlesPayload = (state) => state.retrofit.fetchBundlesPayload;

export const selectAxesToDisplay = (state) => state.retrofit.axesToDisplay;
export const selectItemToHighlight = (state) => state.retrofit.itemToHighlight;
export const selectHighlightApplicationSettingsErrors = (state) => state.retrofit.highlightApplicationSettingsErrors;
export const selectApplicationSettingsChanged = (state) => state.retrofit.applicationSettingsChanged;

export const selectLaunchSyncAppData = (state) => state.retrofit.launchSyncAppData;

export const selectPartnerPreferences = (state) => state.retrofit.partnerPreferences;
export const selectEditingAxis = (state) => state.retrofit.editingAxis;

export const selectRetrofitSavedConfigurations = (state) => state.retrofit.savedConfigurations;
export const selectSizingMechanics = (state) => state.retrofit.configuration?.configData?.sizing?.mechanics;
export const selectSizingDynamics = (state) => state.retrofit.configuration?.configData?.sizing?.dynamics;
export const selectSizingGearBox = (state) => state.retrofit.configuration?.configData?.sizing?.gearBox;
